<template>
  <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
    <Card no-margin>
      <CardBody class="h-full">
        <CardTitle
          :title="inputTitle"
          :subtitle="subtitle"
          :has-separator="false"
        />

        <div class="mt-5 flex flex-col gap-5">
          <div
            v-for="config in inputConfigs"
            :key="config.key"
            class="py-1"
          >
            <div class="mb-3 flex items-center justify-between gap-3">
              <div class="font-medium text-primary">{{ config.label }}</div>
            </div>

            <div class="flex items-center gap-4">
              <Slider
                v-model="inputValues[config.key]"
                :min="config.min"
                :max="config.max"
                :step="config.step ?? 1"
                class="w-full"
                @change="handleSliderChange(config)"
              />

              <InputGroup class="w-32 flex-shrink-0">
                <InputGroupAddon v-if="config.prefix">
                  {{ config.prefix }}
                </InputGroupAddon>
                <Input
                  :model-value="getInputModelValue(config)"
                  type="number"
                  classes="text-right w-full"
                  :min="config.min"
                  :max="config.max"
                  @update:model-value="handleInputChange(config, $event)"
                  @focus="handleInputFocus(config)"
                  @blur="handleInputBlur(config, $event)"
                />
                <InputGroupAddon v-if="config.suffix">
                  {{ config.suffix }}
                </InputGroupAddon>
              </InputGroup>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>

    <Card no-margin>
      <CardBody class="h-full">
        <CardTitle
          :title="outputTitle"
          :subtitle="title"
          :has-separator="false"
        />

        <div class="mt-5">
          <div class="pb-4">
            <div class="text-sm uppercase tracking-wide text-brand-solid">{{ mainOutputConfig.label }}</div>
            <div class="mt-2 text-4xl font-semibold text-brand-solid">
              {{ formatOutputValue(mainOutputConfig, outputValues[mainOutputConfig.key] ?? 0) }}
            </div>
          </div>

          <div class="border-t border-secondary">
            <div
              v-for="config in secondaryOutputConfigs"
              :key="config.key"
              class="flex items-center justify-between py-3 border-b border-secondary last:border-b-0"
            >
              <div class="text-sm text-secondary">{{ config.label }}</div>
              <div class="text-lg font-semibold text-primary">
                {{ formatOutputValue(config, outputValues[config.key] ?? 0) }}
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  </div>
</template>

<script setup lang="ts">
// vue
import { computed, ref, watch } from 'vue'
// components
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import CardTitle from '@/components/card/CardTitle.vue'
import Input from '@/components/forms/Input.vue'
import InputGroup from '@/components/forms/InputGroup.vue'
import InputGroupAddon from '@/components/forms/InputGroupAddon.vue'
import Slider from 'primevue/slider'

export type RoiOutputFormat = 'number' | 'currency' | 'percent'

export interface RoiInputConfig {
  key: string
  label: string
  min: number
  max: number
  defaultValue: number
  step?: number
  decimals?: number
  prefix?: string
  suffix?: string
}

export interface RoiOutputConfig {
  key: string
  label: string
  format?: RoiOutputFormat
  decimals?: number
  calculate: (inputs: Record<string, number>, outputs: Record<string, number>) => number
}

/**
 * Defines the calculator configuration and display labels.
 */
const props = withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
    inputTitle?: string
    outputTitle?: string
    mainOutputKey?: string
    inputConfigs: RoiInputConfig[]
    outputConfigs: RoiOutputConfig[]
  }>(),
  {
    title: 'Calculator',
    subtitle: 'Adjust values to see the impact',
    inputTitle: 'Inputs',
    outputTitle: 'Results',
    mainOutputKey: 'roi'
  }
)

/**
 * Stores the live values used by sliders and number inputs.
 */
const inputValues = ref<Record<string, number>>({})
const inputDraftValues = ref<Record<string, string>>({})

/**
 * Initializes and keeps input values aligned to the latest input configuration.
 */
watch(
  () => props.inputConfigs,
  configs => {
    const nextValues: Record<string, number> = {}

    configs.forEach(config => {
      const currentValue = inputValues.value[config.key]
      if (typeof currentValue === 'number' && Number.isFinite(currentValue)) {
        nextValues[config.key] = clampValue(currentValue, config.min, config.max)
        return
      }
      nextValues[config.key] = clampValue(config.defaultValue, config.min, config.max)
    })

    inputValues.value = nextValues
  },
  { immediate: true, deep: true }
)

/**
 * Computes all outputs in declaration order so later formulas can reuse earlier results.
 */
const outputValues = computed<Record<string, number>>(() => {
  const results: Record<string, number> = {}

  props.outputConfigs.forEach(config => {
    try {
      const rawValue = config.calculate(inputValues.value, results)
      results[config.key] = sanitizeNumber(rawValue)
    } catch (error) {
      console.error(`Failed to calculate output "${config.key}"`, error)
      results[config.key] = 0
    }
  })

  return results
})

/**
 * Returns the highlighted output definition, defaulting to the first output.
 */
const mainOutputConfig = computed<RoiOutputConfig>(() => {
  const configuredOutput = props.outputConfigs.find(config => config.key === props.mainOutputKey)
  return configuredOutput ?? props.outputConfigs[0]
})

/**
 * Returns all non-highlighted outputs.
 */
const secondaryOutputConfigs = computed<RoiOutputConfig[]>(() => {
  return props.outputConfigs.filter(config => config.key !== mainOutputConfig.value.key)
})

/**
 * Handles manual input edits and enforces number, range, and step constraints.
 * @param config input definition for this field
 * @param value browser input value
 */
function handleInputChange(config: RoiInputConfig, value: string): void {
  try {
    inputDraftValues.value[config.key] = value

    if (value === '') {
      return
    }

    const parsedValue = Number(value)
    if (!Number.isFinite(parsedValue)) {
      return
    }

    inputValues.value[config.key] = clampValue(parsedValue, config.min, config.max)
  } catch (error) {
    console.error(`Failed to update input "${config.key}"`, error)
  }
}

/**
 * Marks the field as actively edited and keeps raw user text.
 * @param config input definition for this field
 */
function handleInputFocus(config: RoiInputConfig): void {
  try {
    inputDraftValues.value[config.key] = toDisplayNumber(inputValues.value[config.key], config.decimals ?? 0)
  } catch (error) {
    console.error(`Failed to focus input "${config.key}"`, error)
  }
}

/**
 * Finalizes typed value on blur without step snapping.
 * @param config input definition for this field
 * @param value browser input value
 */
function handleInputBlur(config: RoiInputConfig, value: string): void {
  try {
    const parsedValue = Number(value)
    if (Number.isFinite(parsedValue)) {
      inputValues.value[config.key] = clampValue(parsedValue, config.min, config.max)
    }

    delete inputDraftValues.value[config.key]
  } catch (error) {
    console.error(`Failed to blur input "${config.key}"`, error)
  }
}

/**
 * Ensures slider changes are normalized to the expected step and bounds.
 * @param config input definition for this field
 */
function handleSliderChange(config: RoiInputConfig): void {
  try {
    inputValues.value[config.key] = normalizeInputValue(inputValues.value[config.key], config)
  } catch (error) {
    console.error(`Failed to normalize slider "${config.key}"`, error)
  }
}

/**
 * Applies clamping, finite checks, and step rounding for an input value.
 * @param value incoming raw value
 * @param config target input definition
 * @returns normalized number
 */
function normalizeInputValue(value: number, config: RoiInputConfig): number {
  const safeNumber = Number.isFinite(value) ? value : config.defaultValue
  const steppedValue = applyStep(safeNumber, config.step ?? 1, config.min)
  return clampValue(steppedValue, config.min, config.max)
}

/**
 * Clamps a value to a min/max range.
 * @param value number to clamp
 * @param min minimum allowed value
 * @param max maximum allowed value
 * @returns clamped number
 */
function clampValue(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

/**
 * Rounds a value to the nearest valid step from a base.
 * @param value number to normalize
 * @param step step size to snap to
 * @param min minimum/base value
 * @returns stepped number
 */
function applyStep(value: number, step: number, min: number): number {
  if (!Number.isFinite(step) || step <= 0) {
    return value
  }

  const precision = countDecimals(step)
  const stepped = Math.round((value - min) / step) * step + min
  return Number(stepped.toFixed(precision))
}

/**
 * Converts a number into a safe finite value for display.
 * @param value value to sanitize
 * @returns finite number
 */
function sanitizeNumber(value: number): number {
  return Number.isFinite(value) ? value : 0
}

/**
 * Counts decimal places of a numeric step for accurate rounding.
 * @param value numeric value
 * @returns decimal precision count
 */
function countDecimals(value: number): number {
  const parts = value.toString().split('.')
  return parts[1]?.length ?? 0
}

/**
 * Formats a number used in input displays and fields.
 * @param value numeric value
 * @param decimals decimal places
 * @returns fixed string
 */
function toDisplayNumber(value: number, decimals: number): string {
  return sanitizeNumber(value).toFixed(decimals)
}

/**
 * Returns the live input display string, preferring raw draft text while typing.
 * @param config input definition
 * @returns display-ready text
 */
function getInputModelValue(config: RoiInputConfig): string {
  if (Object.hasOwn(inputDraftValues.value, config.key)) {
    return inputDraftValues.value[config.key]
  }

  return toDisplayNumber(inputValues.value[config.key], config.decimals ?? 0)
}

/**
 * Formats output values according to the output's display mode.
 * @param config output definition
 * @param value calculated value
 * @returns formatted output text
 */
function formatOutputValue(config: RoiOutputConfig, value: number): string {
  const format = config.format ?? 'number'
  const decimals = config.decimals ?? 0
  const safeValue = sanitizeNumber(value)

  try {
    if (format === 'currency') {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(safeValue)
    }

    if (format === 'percent') {
      return `${safeValue.toFixed(decimals)}%`
    }

    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(safeValue)
  } catch (error) {
    console.error('Failed to format output value', error)
    return '0'
  }
}
</script>
